import * as React from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { Trash2, Plus } from "lucide-react";

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    bankAccountId: string | null;
    bankAccountName?: string | null;
    onSaved?: () => void;
};

type Mode = "single" | "bulk";
type BulkMode = "range" | "manual";

type BalanceRow = {
    id: string;
    date: string;
    closingBalance: string;
};

function parseAmount(input: string): number {
    const normalized = input
        .trim()
        .replace(/\s/g, "")
        .replace(/,/g, "");

    if (!/^-?\d+(\.\d+)?$/.test(normalized)) {
        return NaN;
    }

    return Number(normalized);
}

function todayISODate() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

function createRow(date = todayISODate(), closingBalance = ""): BalanceRow {
    return {
        id: crypto.randomUUID(),
        date,
        closingBalance,
    };
}

function enumerateDates(start: string, end: string): string[] {
    if (!start || !end) return [];

    const startDate = new Date(`${start}T00:00:00`);
    const endDate = new Date(`${end}T00:00:00`);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return [];
    if (startDate > endDate) return [];

    const result: string[] = [];
    const current = new Date(startDate);

    while (current <= endDate) {
        const yyyy = current.getFullYear();
        const mm = String(current.getMonth() + 1).padStart(2, "0");
        const dd = String(current.getDate()).padStart(2, "0");
        result.push(`${yyyy}-${mm}-${dd}`);
        current.setDate(current.getDate() + 1);
    }

    return result;
}

export default function UpdateAccountBalanceDialog({
    open,
    onOpenChange,
    bankAccountId,
    bankAccountName,
    onSaved,
}: Props) {
    const { user: profile, loading: profileLoading } = useAuth();

    const editorName = React.useMemo(() => {
        if (profileLoading) return "Loading...";
        const full = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ");
        return full || profile?.email || "Unknown user";
    }, [profile, profileLoading]);

    const [currency, setCurrency] = React.useState<string>("ETB");

    const [mode, setMode] = React.useState<Mode>("single");
    const [bulkMode, setBulkMode] = React.useState<BulkMode>("range");

    // single mode
    const [balanceDate, setBalanceDate] = React.useState<string>(todayISODate());
    const [closingBalance, setClosingBalance] = React.useState<string>("");

    // bulk range mode
    const [rangeStart, setRangeStart] = React.useState<string>(todayISODate());
    const [rangeEnd, setRangeEnd] = React.useState<string>(todayISODate());

    // bulk rows (for both range-generated and manual)
    const [rows, setRows] = React.useState<BalanceRow[]>([createRow()]);

    const [loadingAccount, setLoadingAccount] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        if (!bankAccountId) return;

        let cancelled = false;

        (async () => {
            try {
                setLoadingAccount(true);

                const { data, error } = await supabase
                    .from("bank_accounts")
                    .select("currency")
                    .eq("id", bankAccountId)
                    .single();

                if (error) throw error;

                if (!cancelled) {
                    const today = todayISODate();

                    setCurrency(data?.currency || "ETB");

                    setMode("single");
                    setBulkMode("range");

                    setBalanceDate(today);
                    setClosingBalance("");

                    setRangeStart(today);
                    setRangeEnd(today);

                    setRows([createRow(today, "")]);
                }
            } catch (e: any) {
                console.error("Failed to load bank account currency:", e);
                if (!cancelled) {
                    setCurrency("ETB");
                }
                toast.error("Failed to load account data");
            } finally {
                if (!cancelled) setLoadingAccount(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, bankAccountId]);

    const addManualRow = () => {
        setRows((prev) => [...prev, createRow()]);
    };

    const removeRow = (id: string) => {
        setRows((prev) => prev.filter((row) => row.id !== id));
    };

    const updateRow = (id: string, patch: Partial<BalanceRow>) => {
        setRows((prev) =>
            prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
        );
    };

    const generateRowsFromRange = () => {
        const dates = enumerateDates(rangeStart, rangeEnd);

        if (dates.length === 0) {
            toast.error("Please select a valid date range");
            return;
        }

        setRows(dates.map((date) => createRow(date, "")));
    };

    const singleValid =
        Boolean(bankAccountId) &&
        Boolean(balanceDate) &&
        closingBalance.trim() !== "" &&
        !Number.isNaN(parseAmount(closingBalance));

    const bulkValid =
        Boolean(bankAccountId) &&
        rows.length > 0 &&
        rows.every(
            (row) =>
                row.date &&
                row.closingBalance.trim() !== "" &&
                !Number.isNaN(parseAmount(row.closingBalance))
        );

    const canSave =
        !saving &&
        !loadingAccount &&
        (mode === "single" ? singleValid : bulkValid);

    const onSave = async () => {
        if (!bankAccountId) return;

        try {
            setSaving(true);

            const { data: authData, error: authErr } = await supabase.auth.getUser();
            if (authErr) throw authErr;

            const userId = authData.user?.id;
            if (!userId) {
                toast.error("You must be logged in");
                return;
            }

            if (mode === "single") {
                const amount = parseAmount(closingBalance);

                if (Number.isNaN(amount)) {
                    toast.error("Current Amount must be a valid number");
                    return;
                }

                const payload = {
                    bank_account_id: bankAccountId,
                    balance_date: balanceDate,
                    closing_balance: amount,
                    currency: currency || "ETB",
                    user_id: userId,
                };

                const { error } = await supabase
                    .from("bank_account_balances")
                    .insert(payload);

                if (error) throw error;

                toast.success("Balance saved");
            } else {
                const payload = rows.map((row) => ({
                    bank_account_id: bankAccountId,
                    balance_date: row.date,
                    closing_balance: parseAmount(row.closingBalance),
                    currency: currency || "ETB",
                    user_id: userId,
                }));

                const { error } = await supabase
                    .from("bank_account_balances")
                    .insert(payload);

                if (error) throw error;

                toast.success(`${payload.length} balance record(s) saved`);
            }

            onSaved?.();
            onOpenChange(false);
        } catch (e: any) {
            console.error("Failed to insert bank_account_balances:", e);
            toast.error(e?.message ? `Failed to save: ${e.message}` : "Failed to save balance");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle>Update current closing balance</DialogTitle>
                    <div className="text-sm text-muted-foreground">
                        {bankAccountName ? (
                            <>
                                Account: <span className="font-medium text-foreground">{bankAccountName}</span>
                            </>
                        ) : (
                            <>
                                Account: <span className="font-medium text-foreground">{bankAccountId ?? "-"}</span>
                            </>
                        )}
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto pr-1">
                    <div className="space-y-4 py-1">
                        <div className="text-xs text-muted-foreground">Editing as</div>
                        <div className="text-sm font-medium">{editorName}</div>
                    </div>

                    <div className="grid gap-2">
                        <Label>Mode</Label>
                        <div className="inline-flex rounded-md border p-1 w-fit">
                            <button
                                type="button"
                                className={cn(
                                    "px-3 py-1.5 text-sm rounded",
                                    mode === "single" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                                )}
                                onClick={() => setMode("single")}
                                disabled={loadingAccount || saving}
                            >
                                Single
                            </button>
                            <button
                                type="button"
                                className={cn(
                                    "px-3 py-1.5 text-sm rounded",
                                    mode === "bulk" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                                )}
                                onClick={() => setMode("bulk")}
                                disabled={loadingAccount || saving}
                            >
                                Bulk mode
                            </button>
                        </div>
                    </div>

                    <div className="grid gap-2">
                        <Label htmlFor="currency">Currency</Label>
                        <Input id="currency" value={currency} disabled />
                        <p className="text-xs text-muted-foreground">
                            Loaded from bank account settings.
                        </p>
                    </div>

                    {mode === "single" ? (
                        <div className="grid gap-3">
                            <div className="grid gap-2">
                                <Label htmlFor="balanceDate">Date of closing balance</Label>
                                <Input
                                    id="balanceDate"
                                    type="date"
                                    value={balanceDate}
                                    onChange={(e) => setBalanceDate(e.target.value)}
                                    disabled={loadingAccount || saving}
                                />
                                <p className="text-xs text-muted-foreground">
                                    Default is today. You can change it to set closing balance for another date.
                                </p>
                            </div>

                            <div className="grid gap-2">
                                <Label htmlFor="closingBalance">Current Amount</Label>
                                <Input
                                    id="closingBalance"
                                    inputMode="decimal"
                                    placeholder="e.g. 1250.00"
                                    value={closingBalance}
                                    onChange={(e) => setClosingBalance(e.target.value)}
                                    disabled={loadingAccount || saving}
                                />
                                <p className="text-xs text-muted-foreground">
                                    This will be saved as <b>closing_balance</b> in <b>bank_account_balances</b>.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid gap-2">
                                <Label>Bulk mode type</Label>
                                <div className="inline-flex rounded-md border p-1 w-fit">
                                    <button
                                        type="button"
                                        className={cn(
                                            "px-3 py-1.5 text-sm rounded",
                                            bulkMode === "range" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                                        )}
                                        onClick={() => setBulkMode("range")}
                                        disabled={loadingAccount || saving}
                                    >
                                        Range
                                    </button>
                                    <button
                                        type="button"
                                        className={cn(
                                            "px-3 py-1.5 text-sm rounded",
                                            bulkMode === "manual" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                                        )}
                                        onClick={() => setBulkMode("manual")}
                                        disabled={loadingAccount || saving}
                                    >
                                        Manual
                                    </button>
                                </div>
                            </div>

                            {bulkMode === "range" && (
                                <div className="rounded-md border p-4 space-y-3">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="grid gap-2">
                                            <Label htmlFor="rangeStart">Start date</Label>
                                            <Input
                                                id="rangeStart"
                                                type="date"
                                                value={rangeStart}
                                                onChange={(e) => setRangeStart(e.target.value)}
                                                disabled={loadingAccount || saving}
                                            />
                                        </div>

                                        <div className="grid gap-2">
                                            <Label htmlFor="rangeEnd">End date</Label>
                                            <Input
                                                id="rangeEnd"
                                                type="date"
                                                value={rangeEnd}
                                                onChange={(e) => setRangeEnd(e.target.value)}
                                                disabled={loadingAccount || saving}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={generateRowsFromRange}
                                            disabled={loadingAccount || saving}
                                        >
                                            Generate dates
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <div className="rounded-md border">
                                <div className="grid grid-cols-[1fr_1fr_60px] gap-3 px-4 py-3 border-b text-xs font-medium text-muted-foreground">
                                    <div>Date (closing balance)</div>
                                    <div>Amount (closing balance)</div>
                                    <div />
                                </div>

                                <div className="max-h-[320px] overflow-auto">
                                    {rows.map((row) => (
                                        <div
                                            key={row.id}
                                            className="grid grid-cols-[1fr_1fr_60px] gap-3 px-4 py-3 border-b last:border-b-0"
                                        >
                                            <Input
                                                type="date"
                                                value={row.date}
                                                onChange={(e) => updateRow(row.id, { date: e.target.value })}
                                                disabled={loadingAccount || saving}
                                            />

                                            <Input
                                                inputMode="decimal"
                                                placeholder="e.g. 1250.00"
                                                value={row.closingBalance}
                                                onChange={(e) => updateRow(row.id, { closingBalance: e.target.value })}
                                                disabled={loadingAccount || saving}
                                            />

                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeRow(row.id)}
                                                disabled={rows.length === 1 || loadingAccount || saving}
                                                title="Remove row"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>

                                <div className="p-4 border-t">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={addManualRow}
                                        disabled={loadingAccount || saving}
                                    >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add row
                                    </Button>
                                </div>
                            </div>

                            <p className="text-xs text-muted-foreground">
                                Bulk mode always saves new rows to <b>bank_account_balances</b>. Existing entries for the same date are not overwritten.
                            </p>
                        </div>
                    )}
                </div>

                <DialogFooter className="pt-4 border-t">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button onClick={onSave} disabled={!canSave}>
                        {saving ? "Saving..." : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}